import { createFileRoute } from "@tanstack/react-router";
import React, { useRef } from "react";
import generatePDF, { Resolution, Margin } from "react-to-pdf";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { tableHeadClass, tableCellClass } from "@/components/ui/table-styles";
import { pharmacyClient } from "@/connect";
import { Loading } from "@/layouts/loading";
import { format, parsePhoneNumber } from "libphonenumber-js";
import QRCode from "react-qr-code"
import dayjs from "dayjs";
import { convertToCurrencyRounded } from "@/utils/currency_converter";

const options = {
  method: "open" as const,
  resolution: Resolution.MEDIUM, // MAKE SURE THIS IS NEVER CHANGE. It'll crash in HIGH RESOLUTION.
  page: {
    margin: Margin.SMALL,
    format: "A4",
    orientation: "portrait" as const,
  },
  canvas: {
    mimeType: "image/png" as const,
    qualityRatio: 1,
  },
  overrides: {
    pdf: {
      compress: true,
    },
    canvas: {
      useCORS: true,
    },
  },
};

const PurchaseInvoicePDF = () => {
  const data = Route.useLoaderData();
  const pharmacyMetadata = localStorage.getItem("pharmacyMetadata") ? JSON.parse(localStorage.getItem("pharmacyMetadata")!) : null;
  const pharmacyLocationDetails = localStorage.getItem("pharmacyLocationDetails") ? JSON.parse(localStorage.getItem("pharmacyLocationDetails")!) : null;
  const targetRef = useRef<HTMLDivElement>(null);

  const handleGeneratePDF = () => {
    if (targetRef.current) {
      generatePDF(targetRef, {
        ...options,
        filename: `purchase_invoice_${Date.now()}.pdf`,
      });
    }
  };

  // Remove logoDataUrl and use the original logoUrl directly
  const logoUrl = pharmacyMetadata?.logoUrl || "/icon.png";

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4">
      <button
        onClick={handleGeneratePDF}
        className="mb-6 px-6 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition"
      >
        Open PDF in New Tab
      </button>
      <div
        ref={targetRef}
        className="w-[794px] bg-white p-4"
        style={{ boxShadow: "none" }}
      >
        {/* Header */}
        <div className="flex justify-between items-start pb-2 mb-6">
          <div className="flex items-start gap-3">
            <img
              src={logoUrl}
              alt={pharmacyMetadata?.pharmacyName || "Pharmacy Logo"}
              className="w-14 h-14 object-contain border border-gray-300 rounded-md p-2 bg-white"
            />
            <div>
              <div className="font-bold text-lg text-black">{pharmacyMetadata?.pharmacyName || "Pharmacy Name"}</div>
              <div className="text-xs text-black">
                {pharmacyLocationDetails?.address || "123 Main Street, Dubai, UAE"}
              </div>
              {pharmacyMetadata.taxIdentifiers.map((ti: any) => (
                <div className="text-xs text-black" key={ti.type}>
                    <span className="font-medium">{ti.type}: </span>{ti.value}
                </div>
              ))}
              <div className="text-xs text-black">
                {parsePhoneNumber(pharmacyMetadata?.primaryPhone || "+971 50 123 4567", pharmacyMetadata?.countryCode || "AE").formatInternational()}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-xs text-black font-medium">
              {data.purchaseInvoice?.invoiceDate?.toDate().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
            <div className="w-24 h-4 bg-white" />
          </div>
        </div>
        {/* GRN & QR */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex-1 pr-2">
            <div className="font-semibold text-sm mb-1 text-black">Goods Receipt Note</div>
            <div className="text-xs text-black">{data.purchaseInvoice?.grnNumber}</div>
          </div>
          <div className="flex flex-col items-center ml-4">
            {/* <img
              src={QR_URL}
              alt="QR Code"
              className="w-20 h-20 mb-1 object-contain"
            /> */}
            <QRCode 
                value={
                    // We shall have item count, grn number, invoice number, supplier name, and pharmacy name
                    `GRN: ${data.purchaseInvoice?.grnNumber}, Invoice: ${data.purchaseInvoice?.invoiceNumber}, Supplier: ${data.purchaseInvoice?.supplierName}, Pharmacy: ${pharmacyMetadata?.pharmacyName}`
                }
                size={80}
                className="mb-1"
            />
            <span className="text-[10px] text-blue-700">
              Electronically Generated
            </span>
          </div>
        </div>
        {/* Table Section */}
        <div className="mb-8">
          <div className="overflow-x-auto">
            <Table className="w-full text-xs border border-gray-200">
              <TableHeader>
                <TableRow>
                  <TableHead className={tableHeadClass + " text-center"}>
                    SL No.
                  </TableHead>
                  <TableHead
                    className={tableHeadClass + " text-center"}
                  >
                    Trade Name
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Unit
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Unit Type
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Batch Number
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Expiry Date
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Original Qty
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Bonus Qty
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Cost from Supplier
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Taxable Value
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Tax
                  </TableHead>
                  <TableHead className={tableHeadClass + " text-center"}>
                    Amount
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.inventoryItems?.map((item, idx) => (
              <TableRow className="font-medium" key={idx}>
                <TableCell className={tableCellClass + ' text-center'}>{idx + 1}</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{item.tradeName}</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{item.unit}</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{item.unitType}</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{item.batchNumber}</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{dayjs(item.expiryDate?.toDate()).format("DD/MM/YYYY")}</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{item.inputQuantity?.original}</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{item.inputQuantity?.bonus}</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{
                    convertToCurrencyRounded(item.costFromSupplier)
                  }</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{
                    convertToCurrencyRounded(item.costFromSupplier * item.inputQuantity?.original!)
                  }</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{
                    convertToCurrencyRounded(
                        // Calculate only the tax on the cost from supplier
                        (
                        item.taxProfile!.applicableTaxes.filter(item => {
                            const countryCode = localStorage.getItem('pharmacyMetadata') ? JSON.parse(localStorage.getItem('pharmacyMetadata') || '{}').countryCode : 'IN';
                            if (countryCode === 'IN') {
                                return item.componentCode.includes('GST');
                            } else if (countryCode === 'AE') {
                                return item.componentCode.includes('VAT');
                            }
                            return false;
                        }).reduce((acc, tax) => {
                            const countryCode = localStorage.getItem('pharmacyMetadata') ? JSON.parse(localStorage.getItem('pharmacyMetadata') || '{}').countryCode : 'IN';
                            if (countryCode === 'IN') {
                                const pharmacyState = localStorage.getItem('pharmacyLocationDetails') ? JSON.parse(localStorage.getItem('pharmacyLocationDetails') || '{}').state : '';
                                if (data.purchaseInvoice?.supplierMetadata?.isSez || pharmacyState === data.purchaseInvoice?.supplierMetadata?.state) {
                                  if (tax.componentCode.includes('CGST') || tax.componentCode.includes('SGST')) {
                                      return acc + (tax.ratePercent / 100) * item.costFromSupplier;
                                  }
                                } else {
                                  if (tax.componentCode.includes('IGST')) {
                                      return acc + (tax.ratePercent / 100) * item.costFromSupplier;
                                  }
                                }
                            } else if (countryCode === 'AE') {
                                return acc + (tax.ratePercent / 100) * item.costFromSupplier;
                            }
                            return acc;
                        }, 0) * item.inputQuantity?.original!
                    )
                    )
                  }</TableCell>
                <TableCell className={tableCellClass + ' text-center'}>{convertToCurrencyRounded(
                    item.inputQuantity?.original! * item.costFromSupplier + 
                    (
                        item.taxProfile!.applicableTaxes.filter(item => {
                            const countryCode = localStorage.getItem('pharmacyMetadata') ? JSON.parse(localStorage.getItem('pharmacyMetadata') || '{}').countryCode : 'IN';
                            if (countryCode === 'IN') {
                                return item.componentCode.includes('GST');
                            } else if (countryCode === 'AE') {
                                return item.componentCode.includes('VAT');
                            }
                            return false;
                        }).reduce((acc, tax) => {
                            const countryCode = localStorage.getItem('pharmacyMetadata') ? JSON.parse(localStorage.getItem('pharmacyMetadata') || '{}').countryCode : 'IN';
                            if (countryCode === 'IN') {
                                const pharmacyState = localStorage.getItem('pharmacyLocationDetails') ? JSON.parse(localStorage.getItem('pharmacyLocationDetails') || '{}').state : '';
                                if (data.purchaseInvoice?.supplierMetadata?.isSez || pharmacyState === data.purchaseInvoice?.supplierMetadata?.state) {
                                  if (tax.componentCode.includes('CGST') || tax.componentCode.includes('SGST')) {
                                      return acc + (tax.ratePercent / 100) * item.costFromSupplier;
                                  }
                                } else {
                                  if (tax.componentCode.includes('IGST')) {
                                      return acc + (tax.ratePercent / 100) * item.costFromSupplier;
                                  }
                                }
                            } else if (countryCode === 'AE') {
                                return acc + (tax.ratePercent / 100) * item.costFromSupplier;
                            }
                            return acc;
                        }, 0) * item.inputQuantity?.original!
                    )
                )}</TableCell>
              </TableRow>
            ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {/* Payment Details, Notes, T&C, Total */}
        <div className="flex gap-4 pt-8 border-t border-black bg-white">
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1 text-black">Notes</div>
            <div className="text-xs text-black mb-2">
              Thank you for your business.
            </div>
            <div className="font-semibold text-sm mb-1 text-black">Agreed T&C</div>
            <div className="text-xs text-black">
              Payment due date: {data.purchaseInvoice?.dueDate?.toDate().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}.
            </div>
          </div>
          <div className="flex flex-col items-end justify-between min-w-[160px]">
            <div>
              <div className="text-xs text-black">Subtotal: {
                    data.inventoryItems?.reduce((acc, item) => {
                      return acc + (item.inputQuantity?.original! * item.costFromSupplier);
                    }, 0).toFixed(2)
                }</div>
              <div className="text-xs text-black mb-4">{
                    // Split the tax into components based on the country code and if AE or IN
                    // Example if IN, we have CGST, SGST, IGST. Show each unique tax percentage and its value
                    // Example output for IN: CGST 2.5%: 31.50, SGST 2.5%: 31.50, IGST 5%: 63.00
                    // Example output for AE: VAT 5%: 63.00, VAT 0%: 0.00
                    // Use map to iterate over the unique taxes and calculate the value
                    data.inventoryItems.map(item => {
                        return item.taxProfile?.applicableTaxes.map(tax => {
                            // Determine the country code
                            const countryCode = localStorage.getItem('pharmacyMetadata') ? JSON.parse(localStorage.getItem('pharmacyMetadata') || '{}').countryCode : 'IN';
                            if (countryCode === 'IN') {
                                const pharmacyState = localStorage.getItem('pharmacyLocationDetails') ? JSON.parse(localStorage.getItem('pharmacyLocationDetails') || '{}').state : '';
                                if (data.purchaseInvoice?.supplierMetadata?.isSez || pharmacyState === data.purchaseInvoice?.supplierMetadata?.state) {
                                    if (tax.componentCode.includes('CGST') || tax.componentCode.includes('SGST')) {
                                        return (
                                            <div key={tax.componentCode} className="text-xs text-black">
                                                {tax.componentCode} ({tax.ratePercent}%): {convertToCurrencyRounded((tax.ratePercent / 100) * item.costFromSupplier * item.inputQuantity?.original!)}
                                            </div>
                                        );
                                    }
                                } else {
                                    if (tax.componentCode.includes('IGST')) {
                                        return (
                                            <div key={tax.componentCode} className="text-xs text-black">
                                                {tax.componentCode} ({tax.ratePercent}%): {convertToCurrencyRounded((tax.ratePercent / 100) * item.costFromSupplier * item.inputQuantity?.original!)}
                                            </div>
                                        );
                                    }
                                }
                            } else if (countryCode === 'AE') {
                                return (
                                    <div key={tax.componentCode} className="text-xs text-black">
                                        {tax.componentCode} ({tax.ratePercent}%): {convertToCurrencyRounded((tax.ratePercent / 100) * item.costFromSupplier * item.inputQuantity?.original!)}
                                    </div>
                                );
                            }
                            return null;
                        });
                    })
                }</div>
            </div>
            <div className="text-xl font-bold mb-2 text-black">
              TOTAL: {convertToCurrencyRounded(
                (
                  data.inventoryItems?.reduce((acc: number, item) => {
                    const countryCode = localStorage.getItem('pharmacyMetadata') ? JSON.parse(localStorage.getItem('pharmacyMetadata') || '{}').countryCode : 'IN';
                    if (countryCode === 'IN') {
                      const pharmacyState = localStorage.getItem('pharmacyLocationDetails') ? JSON.parse(localStorage.getItem('pharmacyLocationDetails') || '{}').state : '';
                      if (data.purchaseInvoice?.supplierMetadata?.isSez || pharmacyState === data.purchaseInvoice?.supplierMetadata?.state) {
                        return acc + (item.inputQuantity?.original! * item.costFromSupplier) +
                          item.taxProfile!.applicableTaxes.filter(tax => tax.componentCode.includes('CGST') || tax.componentCode.includes('SGST')).reduce((taxAcc, tax) => {
                            return taxAcc + (tax.ratePercent / 100) * item.costFromSupplier * item.inputQuantity?.original!;
                          }, 0);
                      } else {
                        return acc + (item.inputQuantity?.original! * item.costFromSupplier) +
                          item.taxProfile!.applicableTaxes.filter(tax => tax.componentCode.includes('IGST')).reduce((taxAcc, tax) => {
                            return taxAcc + (tax.ratePercent / 100) * item.costFromSupplier * item.inputQuantity?.original!;
                          }, 0);
                      }
                    } else if (countryCode === 'AE') {
                      return acc + (item.inputQuantity?.original! * item.costFromSupplier) +
                        item.taxProfile!.applicableTaxes.reduce((taxAcc, tax) => {
                          return taxAcc + (tax.ratePercent / 100) * item.costFromSupplier * item.inputQuantity?.original!;
                        }, 0);
                    }
                    return acc;
                  }, 0)
                )
              )}
            </div>
            <div className="mb-2 text-black text-xs">Electronically Generated. Signature not required.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute(
  "/(pharma)/_layout/inventory/purchase-invoice/$id/pdf/"
)({
  component: PurchaseInvoicePDF,
  loader: async ({ params }) => {
    const { id } = params;
    if (!id) {
      throw new Error("Invoice ID is required");
    }
    return await pharmacyClient.getPurchaseInvoice({
      purchaseInvoiceId: id,
    });
  },
  pendingComponent: () => <Loading />,
  pendingMinMs: 1000,
  pendingMs: 1,
});
